import * as React from "react";
import Box from "@mui/material/Box";
import classes from "./SearchResults.module.css";
import Button from "@mui/material/Button";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import SystemDetails from "../SystemDetails/SystemDetails";
import exportAsCsv from "utils/exportAsCsv";
import moment from "moment";
import EditNonHRMSEmployee from "../EditNonHRMSEmployee/EditNonHRMSEmployee";
import PropTypes from "prop-types";
import PrimeReactDataGrid from "components/PrimeReactDataGrid/PrimeReactDataGrid";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import SnackBar from "components/SnackBar/SnackBar";
import { styled } from "@mui/material/styles";
import { Typography, colors } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";

const ComponentToBeDisplayed = ({ selected, handleSelectedOracleId }) => {
  return (
    <>
      {selected?.type === "System" ? (
        <SystemDetails
          handleSelectedOracleId={handleSelectedOracleId}
          oracleOrExtEmpId={selected.oracleOrExtEmpId}
          name={selected.name}
          firstName={selected.firstName}
          lastName={selected.lastName}
        />
      ) : (
        <EditNonHRMSEmployee
          handleSelectedOracleId={handleSelectedOracleId}
          firstName={selected.firstName}
          lastName={selected.lastName}
          oracleOrExtEmpId={selected.oracleOrExtEmpId}
        />
      )}
    </>
  );
};

const SearchResults = (props) => {
  const [selected, setSelected] = React.useState({
    display: false,
    oracleOrExtEmpId: null,
    name: "",
    type: "",
  });

  const [rows, setRows] = React.useState([]);
  const [gridPageSize, setGridPageSize] = React.useState(10);
  const [selectedRows, setSelectedRows] = React.useState([]);
  const [rowData, setRowData] = React.useState([]);
  const [snack, setSnack] = React.useState({
    open: false,
    message: "",
    severity: "",
  });

  const onRowSelectChange = (event) => {
    try {
      if (event.checked) {
        setSelectedRows(rowData);
      } else if (event.originalEvent.target.checked === false) {
        setSelectedRows([]);
      } else {
        setSelectedRows(event.value);
      }
    } catch (error) {
      setSnack({
        open: true,
        message: "Error occurred while changing row selection.",
        severity: "error",
      });
    }
  };

  const calculatePageSize = () => {
    const height = window.innerHeight;
    const width = window.innerWidth;
    if (height > 1200) return 100;
    if (height > 900) return 50;
    if (height > 600) return 25;
    return 10;
  };

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const handleSelectedOracleId = (
    display,
    oracleOrExtEmpId,
    firstName,
    lastName,
    type
  ) => {
    setSelected(() => {
      return {
        display: display,
        oracleOrExtEmpId: oracleOrExtEmpId,
        firstName: firstName,
        lastName: lastName,
        type: type,
      };
    });
  };

  React.useEffect(() => {
    setSelected({
      display: false,
      oracleOrExtEmpId: null,
      name: "",
      type: "",
    });
  }, [rows]);

  const CustomIconButton = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
    color: theme.palette.primary.main,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
      color: theme.palette.primary.dark,
    },
    "& .MuiTypography-root": {
      marginRight: theme.spacing(1),
    },
  }));

  const columns = [
    {
      field: "OracleOrExtEmpId",
      header: "Oracle Id / Ext EmployeeID",
      filter: true,
      sortable: true,
      bodyStyle: { textAlign: "center" },
      filterPlaceholder: "Search by Oracle Id / Ext EmployeeID",
      body: (rowData) => (
        <button
          onClick={() => {
            if (rowData.IsTTEC === "Yes") {
              handleSelectedOracleId(
                true,
                rowData.OracleOrExtEmpId,
                rowData.FirstName,
                rowData.LastName,
                "System"
              );
            } else {
              handleSelectedOracleId(
                true,
                rowData.OracleOrExtEmpId,
                rowData.FirstName,
                rowData.LastName,
                "Edit"
              );
            }
          }}
          className={classes.OracleOrExtEmpId}
        >
          {" "}
          {rowData.OracleOrExtEmpId || "NO ID"}{" "}
        </button>
      ),
    },
    {
      field: "IsTTEC",
      header: "Is TTEC Employee?",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by TTEC Employee",
      bodyStyle: { textAlign: "center" },
      body: (rowData) => (
        <Box display="flex" alignItems="center" gap={2}>
          {rowData.IsTTEC === "Yes" ? (
            <>
              <CheckIcon
                sx={{
                  color: "green",
                  width: "20px",
                  height: "20px",
                  marginLeft: "30px",
                }}
              />
              <Typography sx={{ color: "#3F5765" }}> Yes </Typography>
            </>
          ) : (
            <>
              <ClearIcon
                sx={{
                  color: "red",
                  width: "20px",
                  height: "20px",
                  marginLeft: "30px",
                }}
              />
              <Typography sx={{ color: "#3F5765" }}> No </Typography>
            </>
          )}
        </Box>
      ),
    },
    {
      field: "LastName",
      header: "Last name",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Last Name",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "FirstName",
      header: "First name",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by First Name",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "SupervisorName",
      header: "Supervisor Name",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Supervisor Name",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "OracleStatus",
      header: "Oracle Status",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Oracle Status",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "HiredDate",
      header: "Hired Date",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Hired Date",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "ClientName",
      header: "Client Name",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Client Name",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "ProgramName",
      header: "Program Name",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Program Name",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "ProjectName",
      header: "Project Name",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Project Name",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "LocationName",
      header: "Location Name",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Location Name",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "JobName",
      header: "Job Description",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Job Description",
      bodyStyle: { textAlign: "left" },
    },
  ];

  const filterProps = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    OracleOrExtEmpId: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    IsTTEC: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    FirstName: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    LastName: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    SupervisorName: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    OracleStatus: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    HiredDate: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    ClientName: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    ProgramName: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    ProjectName: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    LocationName: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    JobName: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
  };

  React.useEffect(() => {
    const data = props?.rows?.map((rowItem, index) => {
      return {
        id: index,
        OracleOrExtEmpId: rowItem?.OracleOrExtEmpId,
        FirstName: rowItem?.FirstName,
        LastName: rowItem?.LastName,
        SupervisorName: rowItem?.SupervisorName,
        OracleStatus: rowItem?.OracleStatus,
        HiredDate: rowItem["Hired Date"]
          ? moment(rowItem["Hired Date"]).format("YYYY-MM-DD")
          : "",
        ClientName: rowItem?.ClientName,
        ProgramName: rowItem?.ProgramName,
        ProjectName: rowItem?.ProjectName,
        LocationName: rowItem?.LocationName,
        JobName: rowItem?.JobName,
        IsTTEC: rowItem?.IsTTEC ? "Yes" : "No",
      };
    });
    setRows(data);
    const updatePageSize = () => {
      setGridPageSize(calculatePageSize());
    };
    updatePageSize();
    window.addEventListener("resize", updatePageSize);
    setRowData(data);
  }, [props?.rows]);

  return (
    <>
      <div>
        {!selected.display && rows?.length > 0 && (
          <div className={classes.exportContainer}>
            <Button
              sx={{
                textTransform: "none",
                color: "#2AA154",
                borderColor: "#2AA154",
                borderRadius: "8px",
                fontFamily: "urbanist",
              }}
              variant="outlined"
              startIcon={<FileUploadIcon />}
              onClick={() => exportAsCsv(rows, columns, "CPSearchEmployee")}
            >
              Export
            </Button>
          </div>
        )}
        <Box className={classes.dataGridContainer}>
          {!selected.display ? (
            <PrimeReactDataGrid
              columns={columns}
              removableSort
              resizableColumns
              showGridlines
              paginator
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} record(s)"
              columnResizeMode="expand"
              value={rows}
              selectionMode="multiple"
              dragSelection
              metaKeySelection={false}
              rows={gridPageSize}
              onSelectionChange={onRowSelectChange}
              selection={selectedRows}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              filterprops={filterProps}
              globalFilterFields={[
                "OracleOrExtEmpId",
                "IsTTEC",
                "FirstName",
                "LastName",
                "SupervisorName",
                "OracleStatus",
                "HiredDate",
                "ClientName",
                "ProgramName",
                "ProjectName",
                "LocationName",
                "JobName",
              ]}
            />
          ) : (
            <ComponentToBeDisplayed
              selected={selected}
              handleSelectedOracleId={handleSelectedOracleId}
            />
          )}
        </Box>
      </div>
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>;
    </>
  );
};
SearchResults.propTypes = {
  rows: PropTypes.array.isRequired,
};

ComponentToBeDisplayed.propTypes = {
  selected: PropTypes.object.isRequired,
  handleSelectedOracleId: PropTypes.func.isRequired,
};

export default SearchResults;
